﻿import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { type ApiError, type AttendanceCorrection } from '@pms/shared';
import { usersService, type StaffListItem } from '@/services/users.service';
import { AttendanceSummaryModal } from './AttendanceSummaryModal';
import { PunchTimelineModal } from './PunchTimelineModal';
import { AddPunchModal } from './AddPunchModal';
import { EditPunchModal } from './EditPunchModal';
import { MonthlyOverviewTab } from './MonthlyOverviewTab';
import { CompanyPaidHoursModal } from './CompanyPaidHoursModal';
import { ApplyPenaltyModal } from './ApplyPenaltyModal';
import { PaidHoursTab } from './PaidHoursTab';
import { PenaltyApprovalTab } from './PenaltyApprovalTab';
import { ListPagination } from './ListPagination';
import { LogsPinDialog } from './LogsPinDialog';
import { LOGS_PIN_CODE } from '@/config/env';
import {
  Activity,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Eye,
  Gift,
  History,
  Pencil,
  Plus,
  RotateCcw,
  ShieldCheck,
  UserCircle2,
  Users,
  X,
  XCircle,
  Wallet,
  ClipboardList,
  BarChart3,
  FileText,
} from 'lucide-react';
import { attendanceService, type DailyRecordWithUser, type PunchLogWithUser, type AttendanceChangeLog } from '@/services/attendance.service';
import { useInvalidateAttendance } from '@/hooks/attendance/useInvalidateAttendance';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Tabs } from '@/components/ui/tabs';
import { DateRangePicker } from '@/components/tasks/DateRangePicker';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { monthDateRange } from '@/lib/dateFormat';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { format, parseISO } from 'date-fns';
import { formatAttendanceHours } from '@/lib/duration';

// ── Helpers ────────────────────────────────────────────────────────────────────

function toLocalDate(iso: string | null): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
}

// Shared with PunchTimelineModal so the report row and the timeline tiles format identically.
const formatHours = formatAttendanceHours;

function formatDisplayDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function navigateDate(current: string, delta: number): string {
  const d = new Date(current + 'T00:00:00');
  d.setDate(d.getDate() + delta);
  return getISODate(d);
}

interface ParsedReason {
  correctionType: 'add_missing' | 'edit_time' | null;
  detail: string | null;
  text: string;
}

function parseCorrectionReason(reason: string | null | undefined): ParsedReason {
  if (!reason) return { correctionType: null, detail: null, text: '' };
  const match = reason.match(/^\[(.+?)\s*\|\s*(.+?)\]\s*([\s\S]*)/);
  if (match) {
    const typeRaw = (match[1] ?? '').trim();
    const detail  = (match[2] ?? '').trim();
    const text    = (match[3] ?? '').trim();
    if (typeRaw === 'Add Missing Punch') return { correctionType: 'add_missing', detail, text };
    if (typeRaw === 'Edit Punch Time')   return { correctionType: 'edit_time',   detail, text };
  }
  return { correctionType: null, detail: null, text: reason };
}

type TabId = 'summary' | 'punches' | 'monthly' | 'miss-punch' | 'penalty' | 'paid-hours' | 'logs';

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'summary',    label: 'Summary',              icon: ClipboardList },
  { id: 'punches',    label: 'Punches',              icon: Activity },
  { id: 'monthly',   label: 'Monthly Overview',     icon: BarChart3 },
  { id: 'miss-punch', label: 'Punch Requests', icon: AlertTriangle },
  { id: 'penalty',    label: 'Penalty',              icon: ShieldCheck },
  { id: 'paid-hours', label: 'Paid Hours',           icon: UserCircle2 },
  { id: 'logs',       label: 'Logs',                 icon: FileText },
];

// ── Status badge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { label: string; cls: string }> = {
    present:        { label: 'Present',  cls: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-900/50' },
    late:           { label: 'Late',     cls: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-900/50' },
    half_day:       { label: 'Half Day', cls: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-900/50' },
    work_from_home: { label: 'WFH',      cls: 'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-900/50' },
    absent:         { label: 'Absent',   cls: 'bg-red-100 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-900/50' },
  };
  const { label, cls } = cfg[status] ?? { label: status, cls: 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700' };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
}

function NoPunchOutBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-orange-300 bg-orange-100 px-2.5 py-0.5 text-xs font-semibold text-orange-700">
      <AlertTriangle className="h-3 w-3" /> No Punch Out
    </span>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  colorClass: string;
  numClass: string;
}

function StatCard({ label, value, icon: Icon, colorClass, numClass }: StatCardProps) {
  return (
    <div className={`rounded-lg border border-border/60 p-3 ${colorClass}`}>
      <div className="mb-1.5 flex items-center gap-1.5">
        <Icon className={`h-3.5 w-3.5 ${numClass}`} />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-tight">{label}</span>
      </div>
      <span className={`text-xl font-bold tracking-tight ${numClass}`}>{value}</span>
    </div>
  );
}

// ── Employee row ──────────────────────────────────────────────────────────────

interface EmployeeRowProps {
  record:      DailyRecordWithUser;
  onSummary:   (r: DailyRecordWithUser) => void;
  onTimeline:  (r: DailyRecordWithUser) => void;
  onAddPunch:  (r: DailyRecordWithUser) => void;
}

function EmployeeAttendanceRow({ record, onSummary, onTimeline, onAddPunch }: EmployeeRowProps) {
  const fullName = [record.first_name, record.last_name].filter(Boolean).join(' ') || '–';

  return (
    <div
      className="group relative overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm transition-all hover:shadow-md"
      style={{
        borderLeft: `4px solid ${
          record.status === 'present' ? '#10b981'
          : record.status === 'late' ? '#f59e0b'
          : record.status === 'half_day' ? '#f97316'
          : record.status === 'absent' ? '#ef4444'
          : '#94a3b8'
        }`,
      }}
    >
      {/* Top row: avatar + name + actions */}
      <div className="flex items-start gap-3 px-4 py-3">
        {/* Avatar */}
        <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full border border-border/60 bg-muted">
          <div className="flex h-full w-full items-center justify-center">
            <UserCircle2 className="h-5 w-5 text-muted-foreground" />
          </div>
          {record.photo_url && (
            <img
              src={record.photo_url}
              alt={fullName}
              className="absolute inset-0 h-full w-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
        </div>

        {/* Name + badges + code */}
        <div className="min-w-0 flex-1">
          <Link
            to={`/hrms/staff/${record.user_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block truncate font-semibold uppercase tracking-wide text-primary hover:underline text-sm"
          >
            {fullName}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <StatusBadge status={record.status} />
            {record.status === 'half_day' && !record.last_punch_out && <NoPunchOutBadge />}
            {(record.paid_hours_approved ?? 0) > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-100 px-2 py-0.5 text-[10px] font-semibold text-teal-700 dark:border-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
                <Gift className="h-2.5 w-2.5" />{(record.paid_hours_approved ?? 0)}h Paid
              </span>
            )}
            {(record.paid_hours_pending ?? 0) > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                <Clock className="h-2.5 w-2.5" />{(record.paid_hours_pending ?? 0)}h Pending
              </span>
            )}
          </div>
          <span className="mt-0.5 block text-xs text-muted-foreground">{record.employee_code ?? '–'}</span>
        </div>

        {/* Action icons — always visible on mobile */}
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            title="Attendance Summary"
            onClick={() => onSummary(record)}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            title="Punch Timeline"
            onClick={() => onTimeline(record)}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <History className="h-4 w-4" />
          </button>
          <button
            title="Add Missing Punch"
            onClick={() => onAddPunch(record)}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Bottom row: punch details */}
      <div className="grid grid-cols-2 gap-0 border-t border-border/40 sm:grid-cols-3 md:grid-cols-6">
        {[
          { label: 'Punch In',  value: toLocalDate(record.first_punch_in),                                          grace: false },
          { label: 'Punch Out', value: toLocalDate(record.last_punch_out),                                          grace: false },
          { label: 'Working',   value: formatHours(record.total_working_minutes),                                    grace: (record.grace_minutes ?? 0) > 0 },
          { label: 'Break',     value: formatHours(record.break_minutes),                                            grace: false },
          { label: 'Paid Hours',  value: formatHours(record.effective_working_minutes),                                grace: false },
          { label: 'Penalty',   value: (record.penalty_approved_minutes ?? 0) > 0 ? formatHours(record.penalty_approved_minutes) : '–', grace: false },
        ].map((cell, i) => {
          const isPunchOut = cell.label === 'Punch Out';
          const punchOutMissing = isPunchOut && record.has_unbalanced_punches;
          return (
          <div
            key={cell.label}
            className={[
              'flex flex-col px-4 py-2.5',
              // 2-col mobile: odd indices get left border
              // 3-col sm: i%3!==0 gets left border
              // 6-col md: i>0 gets left border
              [
                '',                                                    // i=0
                'border-l border-border/40',                           // i=1
                'sm:border-l sm:border-border/40',                     // i=2
                'border-l sm:border-l-0 md:border-l border-border/40', // i=3
                'sm:border-l sm:border-border/40',                     // i=4
                'border-l border-border/40',                           // i=5
              ][i],
            ].join(' ')}
          >
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {cell.label}
            </span>
            {punchOutMissing ? (
              <span className="mt-0.5 inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white w-fit">
                <AlertTriangle className="h-2.5 w-2.5" /> Missing
              </span>
            ) : (
            <span
              className={`mt-0.5 text-sm font-semibold ${
                cell.value === '–' ? 'text-muted-foreground/30' :
                cell.label === 'Punch In' ? 'text-primary' : 'text-foreground'
              }`}
            >
              {cell.value}
            </span>
            )}
            {cell.grace && (
              <span className="mt-0.5 inline-flex w-fit items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                +1h grace
              </span>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Skeleton loader ───────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="rounded-xl border border-border/60 bg-card overflow-hidden animate-pulse">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="h-9 w-9 rounded-full bg-muted" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-40 rounded bg-muted" />
          <div className="h-3 w-20 rounded bg-muted/60" />
        </div>
      </div>
      <div className="grid grid-cols-6 border-t border-border/40">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="px-4 py-3 border-l border-border/40 first:border-l-0">
            <div className="h-2 w-12 rounded bg-muted/60 mb-1" />
            <div className="h-4 w-16 rounded bg-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Punch type badge ──────────────────────────────────────────────────────────

function PunchTypeBadge({ type }: { type: string }) {
  if (type === 'check_in') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground shadow-sm">
        <span className="opacity-70">↗</span> Check In
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-0.5 text-xs font-semibold text-primary shadow-sm">
      <span className="opacity-70">↙</span> Check Out
    </span>
  );
}

// ── Punches tab ───────────────────────────────────────────────────────────────

function PunchesTab({ selectedDate, onDateChange }: {
  selectedDate: string;
  onDateChange: (d: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [punchTypeFilter, setPunchTypeFilter] = useState<'all' | 'check_in' | 'check_out'>('all');
  const [editTarget, setEditTarget] = useState<PunchLogWithUser | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['attendance', 'punches', selectedDate, page, punchTypeFilter],
    queryFn: () =>
      attendanceService.listPunchLogs({
        date_from:  selectedDate,
        date_to:    selectedDate,
        page,
        limit:      50,
        punch_type: punchTypeFilter === 'all' ? undefined : punchTypeFilter,
      }),
  });

  const logs = data?.data ?? [];

  const filtered = useMemo(() => {
    if (!search.trim()) return logs;
    const q = search.toLowerCase();
    return logs.filter((l) =>
      [l.first_name, l.last_name, l.employee_code]
        .filter(Boolean).join(' ').toLowerCase().includes(q),
    );
  }, [logs, search]);

  const isToday = selectedDate === getISODate(new Date());

  const columns: Column<PunchLogWithUser>[] = [
    {
      key: 'photo', header: '', className: 'w-12',
      cell: (log) => {
        const name = [log.first_name, log.last_name].filter(Boolean).join(' ') || '–';
        return (
          <div className="h-9 w-9 overflow-hidden rounded-full border border-border/60 bg-muted">
            {(log.raw_image_url ?? log.photo_url)
              ? <img src={(log.raw_image_url ?? log.photo_url)!} alt={name} className="h-full w-full object-cover" />
              : <div className="flex h-full w-full items-center justify-center">
                  <UserCircle2 className="h-5 w-5 text-muted-foreground" />
                </div>}
          </div>
        );
      },
    },
    {
      key: 'time', header: 'Time', className: 'w-[120px]',
      cell: (log) => (
        <span className="font-mono tabular-nums text-sm font-semibold">
          {new Date(log.punch_time).toLocaleTimeString('en-IN', {
            hour: '2-digit', minute: '2-digit',
            hour12: true, timeZone: 'Asia/Kolkata',
          })}
        </span>
      ),
    },
    {
      key: 'employee', header: 'Employee',
      cell: (log) => {
        const name = [log.first_name, log.last_name].filter(Boolean).join(' ') || '–';
        return (
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold uppercase tracking-wide">{name}</p>
            <p className="text-xs text-muted-foreground">{log.employee_code ?? '–'}</p>
          </div>
        );
      },
    },
    {
      key: 'type', header: 'Type', className: 'w-[140px]',
      cell: (log) => <PunchTypeBadge type={log.punch_type} />,
    },
    {
      key: 'source', header: 'Source', className: 'w-[110px]',
      cell: (log) => {
        const cfg =
          log.punch_source === 'face_scan' ? { label: 'Face Scan', cls: 'bg-teal-50 border-teal-200 text-teal-700' }
          : log.punch_source === 'manual'  ? { label: 'Manual',    cls: 'bg-orange-50 border-orange-200 text-orange-700' }
          : log.punch_source === 'kiosk'   ? { label: 'Kiosk',     cls: 'bg-violet-50 border-violet-200 text-violet-700' }
          : { label: log.punch_source ?? '–', cls: 'bg-muted/30 border-border/60 text-muted-foreground' };
        return (
          <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${cfg.cls}`}>
            {cfg.label}
          </span>
        );
      },
    },
    {
      key: 'confidence', header: 'Confidence', className: 'w-[100px]',
      cell: (log) => {
        const score = log.face_verification_score;
        const label = score != null ? `${Math.round(score)}%` : '–';
        const color = score == null ? 'text-muted-foreground'
          : score >= 70 ? 'text-emerald-600'
          : score >= 50 ? 'text-amber-500' : 'text-destructive';
        return <span className={`text-sm font-semibold ${color}`}>{label}</span>;
      },
    },
    {
      key: 'verified', header: 'Verified', className: 'w-[80px] text-center',
      cell: (log) => log.is_verified
        ? <ShieldCheck className="mx-auto h-4 w-4 text-emerald-500" />
        : <XCircle className="mx-auto h-4 w-4 text-muted-foreground/40" />,
    },
    {
      key: 'workMode', header: 'Work Mode', className: 'w-[110px]',
      cell: (log) => {
        const wm = log.work_mode;
        return (
          <span className="text-sm text-muted-foreground">
            {wm ? wm.charAt(0).toUpperCase() + wm.slice(1) : '–'}
          </span>
        );
      },
    },
    {
      key: 'actions', header: '', className: 'w-12',
      cell: (log) => (
        <button
          onClick={() => setEditTarget(log)}
          className="flex items-center justify-center rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Section title */}
      <div className="flex items-center gap-2">
        <Activity className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-base font-semibold text-foreground">Punch Logs</h2>
        {!isLoading && (
          <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {data?.pagination.total ?? 0}
          </span>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Search */}
        <SearchInput
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          placeholder="Search by name or code…"
          containerClassName="w-full sm:max-w-xs"
        />

        {/* Right controls */}
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          {/* Punch type filter */}
          <div className="flex items-center rounded-lg border border-border/60 bg-card overflow-hidden text-sm">
            {(['all', 'check_in', 'check_out'] as const).map((f) => (
              <button
                key={f}
                onClick={() => { setPunchTypeFilter(f); setPage(1); }}
                className={`px-3 py-1.5 font-medium transition-colors ${
                  punchTypeFilter === f
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {f === 'all' ? 'All' : f === 'check_in' ? 'In' : 'Out'}
              </button>
            ))}
          </div>

          {/* Date navigator */}
          <div className="flex items-center gap-1 rounded-lg border border-border/60 bg-card px-1 py-1">
            <button
              onClick={() => { onDateChange(navigateDate(selectedDate, -1)); setPage(1); }}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => { onDateChange(getISODate(new Date())); setPage(1); }}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
                isToday ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
              }`}
            >
              {isToday ? 'Today' : formatDisplayDate(selectedDate)}
            </button>
            <button
              onClick={() => { onDateChange(navigateDate(selectedDate, 1)); setPage(1); }}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(log) => log.id}
        loading={isLoading}
        loadingRows={8}
        error={isError}
        onRetry={() => void refetch()}
        empty={
          <div className="flex flex-col items-center gap-2 py-6">
            <Activity className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {search ? 'No punches match your search' : 'No punch logs for this date'}
            </p>
            {search && (
              <button onClick={() => setSearch('')} className="text-xs text-primary hover:underline">
                Clear search
              </button>
            )}
          </div>
        }
        pagination={data ? {
          page: data.pagination.page,
          totalPages: data.pagination.totalPages,
          total: data.pagination.total,
          onPageChange: setPage,
        } : undefined}
      />

      {editTarget && (
        <EditPunchModal
          log={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={() => { void refetch(); setEditTarget(null); }}
        />
      )}
    </div>
  );
}

// ── User filter select — searchable dropdown with photo + clear button ────────

function UserFilterSelect({
  usersList,
  isLoading,
  isError,
  selectedUserId,
  onChange,
}: {
  usersList: StaffListItem[];
  isLoading: boolean;
  isError: boolean;
  selectedUserId: string;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return usersList;
    const q = search.toLowerCase();
    return usersList.filter((u) =>
      [u.first_name, u.last_name, u.employee_code]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [usersList, search]);

  const selectedUser = selectedUserId ? usersList.find((u) => u.id === selectedUserId) : null;

  const triggerLabel = isLoading
    ? 'Loading…'
    : isError
      ? 'Error'
      : selectedUser
        ? [selectedUser.first_name, selectedUser.last_name].filter(Boolean).join(' ')
        : 'All Users';

  const handleSelect = (id: string) => {
    onChange(id);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative flex items-center gap-1">
      {/* Trigger */}
      <button
        onClick={() => { if (!isLoading && !isError) setOpen((o) => !o); }}
        disabled={isLoading || isError}
        className={[
          'flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
          selectedUser
            ? 'border-primary/30 bg-primary/5 text-primary hover:bg-primary/10'
            : 'border-border/60 bg-card text-foreground hover:bg-muted',
        ].join(' ')}
      >
        <Users className="h-4 w-4 shrink-0" />
        <span className="max-w-[130px] truncate">{triggerLabel}</span>
        <ChevronRight
          className={`h-3 w-3 transition-transform ${open ? '-rotate-90' : 'rotate-90'}`}
        />
      </button>

      {/* Clear button — visible only when a specific user is selected */}
      {selectedUserId && (
        <button
          onClick={() => onChange('')}
          title="Clear user filter"
          className="flex h-9 w-9 items-center justify-center rounded-md border border-border/60 bg-card text-muted-foreground transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-500"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-10 z-50 w-64 overflow-hidden rounded-xl border border-border/60 bg-card shadow-xl">
          {/* Search input */}
          <div className="border-b border-border/40 p-2">
            <Input
              autoFocus
              placeholder="Search employee…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
            />
          </div>

          {/* Scrollable list */}
          <div className="max-h-60 overflow-y-auto">
            {/* All Users row */}
            <button
              onClick={() => handleSelect('')}
              className={[
                'flex w-full items-center gap-2 px-3 py-2.5 text-sm transition-colors hover:bg-muted',
                !selectedUserId ? 'bg-primary/5 font-semibold text-primary' : 'text-foreground',
              ].join(' ')}
            >
              <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
              All Users
            </button>

            {/* Employee rows */}
            {filtered.map((u) => {
              const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || '–';
              const isSelected = u.id === selectedUserId;
              return (
                <button
                  key={u.id}
                  onClick={() => handleSelect(u.id)}
                  className={[
                    'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-muted',
                    isSelected ? 'bg-primary/5' : '',
                  ].join(' ')}
                >
                  <div className="h-7 w-7 shrink-0 overflow-hidden rounded-full border border-border/60 bg-muted">
                    {u.photo_url ? (
                      <img src={u.photo_url} alt={name} className="h-full w-full object-cover" />
                    ) : (
                      <UserCircle2 className="h-7 w-7 text-muted-foreground/30" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`truncate text-sm font-medium leading-tight ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                      {name}
                    </p>
                    {u.employee_code && (
                      <p className="text-xs leading-tight text-muted-foreground">{u.employee_code}</p>
                    )}
                  </div>
                  {isSelected && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                </button>
              );
            })}

            {/* Empty state */}
            {filtered.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">No employees found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Error helper ─────────────────────────────────────────────────────────────

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

// ── Miss Punch Approvals tab ──────────────────────────────────────────────────

type RegStatus = 'all' | 'requested' | 'approved' | 'rejected';

const REG_STATUS_CFG = {
  requested: { label: 'Pending',  bg: 'bg-amber-100 dark:bg-amber-900/30',    text: 'text-amber-700 dark:text-amber-300',    border: 'border-amber-200 dark:border-amber-800',    bar: 'border-l-amber-400'   },
  approved:  { label: 'Approved', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-800', bar: 'border-l-emerald-400' },
  rejected:  { label: 'Rejected', bg: 'bg-red-100 dark:bg-rose-900/30',        text: 'text-red-600 dark:text-rose-400',        border: 'border-red-200 dark:border-rose-800',        bar: 'border-l-red-400'     },
  none:      { label: '–',        bg: 'bg-muted',                               text: 'text-muted-foreground',                  border: 'border-border',                              bar: 'border-l-border'      },
} as const;

function MissPunchApprovalModal({
  correction,
  action,
  onClose,
  onSuccess,
}: {
  correction: AttendanceCorrection;
  action: 'approve' | 'reject';
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [remarks, setRemarks] = useState('');
  const isApprove = action === 'approve';
  const fullName = [correction.first_name, correction.last_name].filter(Boolean).join(' ') || '–';
  const dateStr  = correction.attendance_date ?? correction.requested_at.slice(0, 10);

  const mutation = useMutation({
    mutationFn: () =>
      attendanceService.reviewCorrection(
        correction.id,
        isApprove ? 'approved' : 'rejected',
        remarks || undefined,
      ),
    onSuccess: () => {
      toast.success(isApprove ? 'Request approved successfully' : 'Request rejected');
      onSuccess();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return createPortal(
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-md rounded-2xl border border-border/60 bg-card shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border/40 px-5 py-4">
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${
            isApprove ? 'bg-emerald-50 border-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-900/40' : 'bg-red-50 border-red-100 dark:bg-rose-900/20 dark:border-rose-900/40'
          }`}>
            {isApprove
              ? <CheckCircle2 className="h-4.5 w-4.5 text-emerald-600" />
              : <XCircle className="h-4.5 w-4.5 text-red-500" />
            }
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-bold text-foreground">
              {isApprove ? 'Approve Request' : 'Reject Request'}
            </h2>
            <p className="truncate text-xs text-muted-foreground">
              {fullName} · {new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-5 py-4">
          {/* Employee's reason (read-only) */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-700/40 dark:bg-amber-900/20">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
              Employee's Reason
            </p>
            <p className="text-sm text-foreground">{correction.reason || '—'}</p>
          </div>

          {/* Remarks input */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              {isApprove ? 'Remarks (optional)' : 'Rejection Reason *'}
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              placeholder={isApprove ? 'Add any notes…' : 'Explain why this request is being rejected…'}
              className="w-full resize-none rounded-lg border border-border/60 bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border/40 px-5 py-3.5">
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || (!isApprove && !remarks.trim())}
            className={!isApprove ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
          >
            {mutation.isPending ? 'Saving…' : isApprove ? 'Approve' : 'Reject'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function MissPunchCard({
  correction,
  onApprove,
  onReject,
  onRevert,
}: {
  correction: AttendanceCorrection;
  onApprove: () => void;
  onReject: () => void;
  onRevert: () => void;
}) {
  const fullName  = [correction.first_name, correction.last_name].filter(Boolean).join(' ') || '–';
  const regStatus = correction.status as keyof typeof REG_STATUS_CFG;
  const cfg       = REG_STATUS_CFG[regStatus] ?? REG_STATUS_CFG.none;
  const isPending  = regStatus === 'requested';
  const isApproved = regStatus === 'approved';
  const isRejected = regStatus === 'rejected';
  const dateStr   = correction.attendance_date ?? correction.requested_at.slice(0, 10);

  return (
    <div className={`overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm border-l-4 ${cfg.bar}`}>
      {/* Top row */}
      <div className="flex items-center gap-3 px-4 py-2.5">
        <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full border border-border/60 bg-muted">
          {correction.photo_url ? (
            <img src={correction.photo_url} alt={fullName} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <UserCircle2 className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold uppercase tracking-wide text-foreground">{fullName}</p>
          <p className="text-xs text-muted-foreground">{correction.employee_code ?? '–'}</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {isPending && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={onApprove}
                className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onReject}
                className="h-8 border-red-200 text-red-600 hover:bg-red-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-900/20"
              >
                <XCircle className="h-3.5 w-3.5" /> Reject
              </Button>
            </>
          )}
          {!isPending && (
            <Button
              size="sm"
              variant="outline"
              onClick={onRevert}
              className="h-8 gap-1 text-muted-foreground hover:bg-muted/40"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Revert
            </Button>
          )}
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cfg.bg} ${cfg.text} ${cfg.border}`}>
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Metadata grid */}
      <div className="grid grid-cols-2 border-t border-border/40">
        <div className="flex flex-col px-4 py-2">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Date</span>
          <span className="mt-0.5 text-sm font-semibold text-foreground">
            {new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
              day: '2-digit', month: 'short', year: 'numeric',
            })}
          </span>
        </div>
        <div className="flex flex-col border-l border-border/40 px-4 py-2">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Submitted</span>
          <span className="mt-0.5 text-sm font-semibold text-foreground">
            {new Date(correction.requested_at).toLocaleString('en-IN', {
              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
            })}
          </span>
        </div>
      </div>

      {/* Reason — parsed for structured correction type */}
      {(() => {
        const parsed = parseCorrectionReason(correction.reason);
        return (
          <div className="border-t border-amber-100 dark:border-amber-900/30 bg-amber-50/70 dark:bg-amber-900/20 px-4 py-2.5">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-600">
              Reason for Correction
            </p>
            {parsed.correctionType && (
              <div className="mb-1.5 flex items-center gap-1.5">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold
                  ${parsed.correctionType === 'add_missing' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'}`}>
                  {parsed.correctionType === 'add_missing' ? '+ Add Missing Punch' : '✎ Edit Punch Time'}
                </span>
                {parsed.detail && (
                  <span className="text-xs font-semibold text-foreground">{parsed.detail}</span>
                )}
              </div>
            )}
            <p className="text-sm text-foreground">
              {parsed.text || correction.reason || '—'}
            </p>
          </div>
        );
      })()}

      {/* Admin remarks (if resolved) */}
      {(isApproved || isRejected) && correction.admin_remarks && (
        <div className={`border-t px-4 py-2.5 ${
          isApproved ? 'bg-emerald-50/60 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-900/30' : 'bg-red-50/60 dark:bg-rose-900/20 border-red-100 dark:border-rose-900/30'
        }`}>
          <p className={`mb-1 text-[10px] font-semibold uppercase tracking-wider ${
            isApproved ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-rose-400'
          }`}>
            {isApproved ? 'Approved Remarks' : 'Rejection Reason'}
          </p>
          <p className="text-sm text-foreground">{correction.admin_remarks}</p>
        </div>
      )}
    </div>
  );
}

/**
 * How often the open Punch Requests tab re-checks for new or changed requests, so a
 * correction requested from /account/attendance shows up without a manual refresh.
 * Polling, not caching: a background refetch keeps the current cards on screen
 * (isLoading stays false). Same interval as the Leave Approvals page.
 */
const PUNCH_REQUESTS_REFRESH_MS = 60_000;

function MissPunchTab() {
  const invalidateAttendance = useInvalidateAttendance();
  const [statusFilter, setStatusFilter] = useState<RegStatus>('requested');
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const rootRef = useRef<HTMLDivElement>(null);
  // Request-date filters: a single date OR a month — picking one clears the other
  const [filterDate, setFilterDate]   = useState('');
  const [filterMonth, setFilterMonth] = useState<number | ''>('');
  const [filterYear, setFilterYear]   = useState<number>(new Date().getFullYear());
  const [approvalTarget, setApprovalTarget] = useState<{
    correction: AttendanceCorrection;
    action: 'approve' | 'reject';
  } | null>(null);

  const monthRange = filterMonth !== '' ? monthDateRange(filterYear, filterMonth) : undefined;
  const dateFrom = filterDate || monthRange?.from;
  const dateTo   = filterDate || monthRange?.to;
  const hasPeriodFilter = !!dateFrom;

  const clearPeriod = () => { setFilterDate(''); setFilterMonth(''); setPage(1); };

  // Search runs on the server so the total and page count cover only the matches
  const debouncedSearch = useDebouncedValue(search.trim());

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['attendance', 'corrections', statusFilter, page, pageSize, dateFrom, dateTo, debouncedSearch],
    queryFn: () =>
      attendanceService.listAllCorrections({
        status: statusFilter === 'all' ? undefined : statusFilter as 'requested' | 'approved' | 'rejected',
        limit: pageSize,
        page,
        date_from: dateFrom,
        date_to: dateTo,
        q: debouncedSearch || undefined,
      }),
    refetchInterval: PUNCH_REQUESTS_REFRESH_MS,
  });

  // An approve/reject can shrink the list under the current page — step back to the last real page
  const totalPages = data?.pagination.totalPages ?? 0;
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const goToPage = (p: number) => {
    setPage(p);
    rootRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  const filtered = data?.data ?? [];

  const STATUS_FILTERS: { value: RegStatus; label: string }[] = [
    { value: 'requested', label: 'Pending' },
    { value: 'all',       label: 'All' },
    { value: 'approved',  label: 'Approved' },
    { value: 'rejected',  label: 'Rejected' },
  ];

  const handleActionSuccess = () => {
    setApprovalTarget(null);
    invalidateAttendance();
  };

  const revertMutation = useMutation({
    mutationFn: (correctionId: string) => attendanceService.revertCorrection(correctionId),
    onSuccess: () => {
      invalidateAttendance();
      toast.success('Moved back to pending');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div ref={rootRef} className="scroll-mt-4 space-y-4">
      {/* Section title */}
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <h2 className="text-base font-semibold text-foreground">Punch Requests</h2>
        {!isLoading && (
          <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {data?.pagination.total ?? 0}
          </span>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SearchInput
            value={search}
            onChange={(v) => { setSearch(v); setPage(1); }}
            placeholder="Search by name or code…"
            containerClassName="w-full sm:w-64"
          />
          <DatePicker
            value={filterDate}
            onChange={(v) => { setFilterDate(v); if (v) setFilterMonth(''); setPage(1); }}
            placeholder="Request date"
            className="h-9 w-44 text-sm"
            modal={false}
          />
          <Select
            value={filterMonth === '' ? 'all' : String(filterMonth)}
            onValueChange={(v) => {
              setFilterMonth(v === 'all' ? '' : Number(v));
              if (v !== 'all') setFilterDate('');
              setPage(1);
            }}
          >
            <SelectTrigger className="h-9 w-36 text-sm">
              <SelectValue placeholder="All Months" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={String(filterYear)}
            onValueChange={(v) => { setFilterYear(Number(v)); setPage(1); }}
          >
            <SelectTrigger className="h-9 w-24 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[new Date().getFullYear() - 2, new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filterMonth !== '' && (
            <Button variant="outline" size="sm" className="h-9 px-2.5" onClick={() => { setFilterMonth(''); setPage(1); }}>
              ✕
            </Button>
          )}
        </div>

        {/* Status filter */}
        <div className="flex items-center overflow-hidden rounded-lg border border-border/60 bg-card text-sm">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setStatusFilter(f.value); setPage(1); }}
              className={`px-3 py-1.5 font-medium transition-colors ${
                statusFilter === f.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid gap-3 lg:grid-cols-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse overflow-hidden rounded-xl border border-border/60 bg-card">
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="h-9 w-9 rounded-full bg-muted" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 w-36 rounded bg-muted" />
                  <div className="h-3 w-20 rounded bg-muted/60" />
                </div>
                <div className="h-6 w-20 rounded-full bg-muted/60" />
              </div>
              <div className="grid grid-cols-3 border-t border-border/40">
                {[0, 1, 2].map((j) => (
                  <div key={j} className="px-4 py-3 border-l border-border/40 first:border-l-0">
                    <div className="h-2 w-12 rounded bg-muted/60 mb-1" />
                    <div className="h-4 w-20 rounded bg-muted" />
                  </div>
                ))}
              </div>
              <div className="border-t border-amber-100 bg-amber-50/50 px-4 py-3">
                <div className="h-2 w-24 rounded bg-amber-100 mb-2" />
                <div className="h-4 w-full rounded bg-amber-100" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-red-100 bg-red-50 py-16 text-center">
          <XCircle className="mb-3 h-10 w-10 text-red-400" />
          <p className="font-semibold text-red-600">Failed to load requests</p>
          <Button size="sm" variant="outline" className="mt-4" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 bg-card/50 py-20 text-center">
          <CheckCircle2 className="mb-3 h-10 w-10 text-muted-foreground/30" />
          <p className="text-sm font-medium text-muted-foreground">
            {search
              ? 'No requests match your search'
              : hasPeriodFilter ? 'No requests in this period' : 'No miss punch requests found'}
          </p>
          {search ? (
            <button onClick={() => setSearch('')} className="mt-2 text-xs text-primary hover:underline">
              Clear search
            </button>
          ) : hasPeriodFilter && (
            <button onClick={clearPeriod} className="mt-2 text-xs text-primary hover:underline">
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {filtered.map((correction) => (
            <MissPunchCard
              key={correction.id}
              correction={correction}
              onApprove={() => setApprovalTarget({ correction, action: 'approve' })}
              onReject={() => setApprovalTarget({ correction, action: 'reject' })}
              onRevert={() => revertMutation.mutate(correction.id)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.pagination.total > 0 && (
        <ListPagination
          page={data.pagination.page}
          totalPages={data.pagination.totalPages}
          total={data.pagination.total}
          pageSize={pageSize}
          onPageChange={goToPage}
          onPageSizeChange={(n) => { setPageSize(n); goToPage(1); }}
        />
      )}

      {/* Approval modal */}
      {approvalTarget && (
        <MissPunchApprovalModal
          correction={approvalTarget.correction}
          action={approvalTarget.action}
          onClose={() => setApprovalTarget(null)}
          onSuccess={handleActionSuccess}
        />
      )}
    </div>
  );
}

// ── Logs tab ──────────────────────────────────────────────────────────────────

const ACTION_CHIPS: { value: string; label: string }[] = [
  { value: 'all',                        label: 'All' },
  { value: 'add_punch',                  label: 'Add Punch' },
  { value: 'edit_punch',                 label: 'Edit Punch' },
  { value: 'regularization_requested',   label: 'Requested' },
  { value: 'regularization_approved',    label: 'Approved' },
  { value: 'regularization_rejected',    label: 'Rejected' },
];

function actionBadgeCfg(type: string): { label: string; cls: string } {
  switch (type) {
    case 'add_punch':                return { label: 'Add Punch',          cls: 'bg-orange-100 border-orange-200 text-orange-700' };
    case 'edit_punch':               return { label: 'Edit Punch',         cls: 'bg-teal-100 border-teal-200 text-teal-700' };
    case 'regularization_requested': return { label: 'Requested',          cls: 'bg-amber-100 border-amber-200 text-amber-700' };
    case 'regularization_approved':  return { label: 'Miss Punch Approved',cls: 'bg-emerald-100 border-emerald-200 text-emerald-700' };
    case 'regularization_rejected':  return { label: 'Rejected',           cls: 'bg-red-100 border-red-200 text-red-600' };
    default:                         return { label: type,                  cls: 'bg-muted border-border/60 text-muted-foreground' };
  }
}

function LogDataPanel({ label, data }: { label: string; data: Record<string, unknown> | null }) {
  if (!data) return null;
  const time = data['time'] as string | undefined;
  const type = data['type'] as string | undefined;
  const status = data['status'] as string | undefined;
  const reason = data['reason'] as string | undefined;
  return (
    <div className="flex-1 min-w-0 rounded-lg border border-border/60 bg-muted/60 p-2.5">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="space-y-1">
        {type && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium text-muted-foreground w-8 shrink-0">Type</span>
            <span className="text-xs font-semibold text-foreground capitalize">{type.replace('_', ' ')}</span>
          </div>
        )}
        {time && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium text-muted-foreground w-8 shrink-0">Time</span>
            <span className="text-xs font-semibold text-foreground">
              {new Date(time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
            </span>
          </div>
        )}
        {status && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium text-muted-foreground w-8 shrink-0">Status</span>
            <span className="text-xs font-semibold text-foreground capitalize">{status}</span>
          </div>
        )}
        {reason && (
          <div className="flex items-start gap-1.5">
            <span className="text-[10px] font-medium text-muted-foreground w-8 shrink-0 mt-0.5">Reason</span>
            <span className="text-xs text-foreground break-words">{reason}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function LogCard({ log }: { log: AttendanceChangeLog }) {
  const { label, cls } = actionBadgeCfg(log.action_type);
  const targetName = [log.target_first_name, log.target_last_name].filter(Boolean).join(' ') || '–';
  const performedName = [log.performed_first_name, log.performed_last_name].filter(Boolean).join(' ') || '–';
  const showOldNew = log.action_type === 'edit_punch' || log.action_type === 'add_punch';
  const showOnlyNew = log.action_type === 'add_punch';

  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 px-4 py-3">
        <div className="flex items-start gap-3 min-w-0">
          {/* Avatar */}
          <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full border border-border/60 bg-muted">
            {log.target_photo_url ? (
              <img src={log.target_photo_url} alt={targetName} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <UserCircle2 className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-0.5">
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}>
                {label}
              </span>
            </div>
            <p className="text-sm font-bold uppercase tracking-wide text-foreground">{targetName}</p>
            <p className="text-xs text-muted-foreground">{log.target_employee_code ?? '–'}</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs font-medium text-muted-foreground whitespace-nowrap">
            {new Date(log.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}
          </p>
          <p className="text-xs text-muted-foreground whitespace-nowrap">
            {new Date(log.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">by <span className="font-semibold text-foreground">{performedName}</span></p>
        </div>
      </div>

      {/* OLD → NEW panels */}
      {showOldNew && (
        <div className="flex items-stretch gap-2 border-t border-border/40 px-4 py-3">
          {!showOnlyNew && log.old_data && (
            <>
              <LogDataPanel label="OLD" data={log.old_data} />
              <div className="flex items-center text-muted-foreground/30 text-lg font-bold px-1">→</div>
            </>
          )}
          <LogDataPanel label={showOnlyNew ? 'NEW' : 'NEW'} data={log.new_data} />
        </div>
      )}

      {/* Remarks */}
      {log.remarks && (
        <div className="border-t border-border/40 bg-muted/40 px-4 py-2.5">
          <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Remarks</p>
          <p className="text-xs text-foreground">{log.remarks}</p>
        </div>
      )}
    </div>
  );
}

function LogsTab() {
  const [page, setPage]             = useState(1);
  const [search, setSearch]         = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const [actionType, setActionType] = useState('all');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['attendance', 'logs', page, dateFrom, dateTo, actionType, search],
    queryFn: () =>
      attendanceService.listLogs({
        page,
        limit: 30,
        date_from:   dateFrom  || undefined,
        date_to:     dateTo    || undefined,
        action_type: actionType === 'all' ? undefined : actionType,
        search:      search.trim() || undefined,
      }),
  });

  const logs = data?.data ?? [];

  return (
    <div className="space-y-4">
      {/* Section title */}
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-base font-semibold text-foreground">Attendance Change Logs</h2>
        {!isLoading && (
          <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {data?.pagination.total ?? 0}
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Action type chips */}
        <div className="flex flex-wrap items-center gap-1.5">
          {ACTION_CHIPS.map((chip) => (
            <button
              key={chip.value}
              onClick={() => { setActionType(chip.value); setPage(1); }}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                actionType === chip.value
                  ? 'bg-primary border-primary text-primary-foreground'
                  : 'border-border/60 bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Date range + search — pushed right */}
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={(v) => { setDateFrom(v); setPage(1); }}
            onDateToChange={(v) => { setDateTo(v); setPage(1); }}
          />
          <SearchInput
            value={search}
            onChange={(v) => { setSearch(v); setPage(1); }}
            placeholder="Search employee…"
            containerClassName="w-48"
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-xl border border-border/60 bg-card overflow-hidden">
              <div className="flex items-start gap-3 px-4 py-3">
                <div className="h-9 w-9 rounded-full bg-muted shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-4 w-32 rounded bg-muted" />
                  <div className="h-3 w-20 rounded bg-muted/60" />
                </div>
                <div className="h-8 w-24 rounded bg-muted/60" />
              </div>
              <div className="border-t border-border/40 px-4 py-3 flex gap-2">
                <div className="flex-1 h-16 rounded-lg bg-muted/60" />
                <div className="flex-1 h-16 rounded-lg bg-muted/60" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-red-100 bg-red-50 py-16 text-center">
          <XCircle className="mb-3 h-10 w-10 text-red-400" />
          <p className="font-semibold text-red-600">Failed to load logs</p>
          <Button size="sm" variant="outline" className="mt-4" onClick={() => void refetch()}>Retry</Button>
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 bg-card/50 py-20 text-center">
          <FileText className="mb-3 h-10 w-10 text-muted-foreground/30" />
          <p className="text-sm font-medium text-muted-foreground">No change logs found</p>
          {(search || dateFrom || dateTo || actionType !== 'all') && (
            <button
              onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); setActionType('all'); setPage(1); }}
              className="mt-2 text-xs text-teal-600 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {logs.map((log) => <LogCard key={log.id} log={log} />)}
        </div>
      )}

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border/40 pt-4 text-sm">
          <span className="text-muted-foreground">
            Page {data.pagination.page} of {data.pagination.totalPages} · {data.pagination.total} total
          </span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" disabled={page >= data.pagination.totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Placeholder tab content ───────────────────────────────────────────────────

function ComingSoonTab({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 bg-card/50 py-20 text-center">
      <BarChart3 className="mb-3 h-10 w-10 text-muted-foreground/30" />
      <p className="text-sm font-medium text-muted-foreground">{label} — Coming Soon</p>
      <p className="mt-1 text-xs text-muted-foreground">This section is under development</p>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function AttendancePage() {
  const [activeTab, setActiveTab] = useState<TabId>('summary');
  const [selectedDate, setSelectedDate] = useState<string>(getISODate(new Date()));
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Header action modal states
  const [showPaidHoursModal,  setShowPaidHoursModal]  = useState(false);
  const [showPenaltyModal,    setShowPenaltyModal]    = useState(false);

  // Logs PIN gate
  const [showLogsPinDialog, setShowLogsPinDialog] = useState(false);
  const [logsUnlocked,      setLogsUnlocked]      = useState(false);

  // Modal targets
  const [summaryTarget,  setSummaryTarget]  = useState<DailyRecordWithUser | null>(null);
  const [timelineTarget, setTimelineTarget] = useState<DailyRecordWithUser | null>(null);
  const [addPunchTarget, setAddPunchTarget] = useState<DailyRecordWithUser | null>(null);

  // Fetch users for the All Users dropdown
  const {
    data: usersData,
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = useQuery({
    queryKey: ['staff', 'attendance-filter'],
    queryFn: () => usersService.list({ limit: 100, is_active: true }),
  });
  const usersList = usersData?.data ?? [];

  // Fetch daily records for selected date (search passed server-side)
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['attendance', 'daily', selectedDate, selectedUserId, debouncedSearch, statusFilter],
    queryFn: () =>
      attendanceService.listDaily({
        date_from: selectedDate,
        date_to: selectedDate,
        all: true,
        user_id: selectedUserId || undefined,
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
      }),
  });

  const records = data?.data ?? [];
  const filtered = records; // search is server-side; no client-side filter needed

  // Server-side summary — company-wide counts for the selected date (not page-limited)
  const { data: summaryData, isLoading: isSummaryLoading } = useQuery({
    queryKey: ['attendance', 'summary', selectedDate, selectedUserId],
    queryFn: () => attendanceService.getDailySummary(selectedDate, selectedUserId || undefined),
  });

  const stats = {
    present:     summaryData?.present     ?? 0,
    late:        summaryData?.late        ?? 0,
    halfDay:     summaryData?.half_day    ?? 0,
    absent:      summaryData?.absent      ?? 0,
    totalSwipes: summaryData?.total_swipes ?? 0,
    avgHours:    summaryData?.avg_hours    ?? 0,
  };


  const handleExport = () => {
    const rows = [
      ['Name', 'Code', 'Status', 'Punch In', 'Punch Out', 'Working Hours'],
      ...filtered.map((r) => [
        [r.first_name, r.last_name].filter(Boolean).join(' '),
        r.employee_code ?? '',
        r.status,
        toLocalDate(r.first_punch_in),
        toLocalDate(r.last_punch_out),
        formatHours(r.total_working_minutes),
      ]),
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `attendance-${selectedDate}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen  space-y-5">

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Attendance</h1>
          <p className="text-sm text-muted-foreground">View daily attendance records and punch logs</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setShowPaidHoursModal(true)}
          >
            <UserCircle2 className="h-4 w-4" />
            Paid
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setShowPenaltyModal(true)}
          >
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Penalty
          </Button>
        </div>
      </div>

      {/* ── Stat cards ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard
          label="Present"
          value={isSummaryLoading ? '…' : stats.present}
          icon={CheckCircle2}
          colorClass="bg-emerald-50/60 dark:bg-emerald-900/20"
          numClass="text-emerald-700 dark:text-emerald-400"
        />
        <StatCard
          label="Late"
          value={isSummaryLoading ? '…' : stats.late}
          icon={Clock}
          colorClass="bg-amber-50/60 dark:bg-amber-900/20"
          numClass="text-amber-700 dark:text-amber-400"
        />
        <StatCard
          label="Half Day"
          value={isSummaryLoading ? '…' : stats.halfDay}
          icon={Clock}
          colorClass="bg-orange-50/60 dark:bg-orange-900/20"
          numClass="text-orange-700 dark:text-orange-400"
        />
        <StatCard
          label="Absent"
          value={isSummaryLoading ? '…' : stats.absent}
          icon={XCircle}
          colorClass="bg-rose-50/60 dark:bg-rose-900/20"
          numClass="text-rose-700 dark:text-rose-400"
        />
        <StatCard
          label="Swipes"
          value={isSummaryLoading ? '…' : stats.totalSwipes}
          icon={Activity}
          colorClass="bg-violet-50/60 dark:bg-violet-900/20"
          numClass="text-violet-700 dark:text-violet-400"
        />
        <StatCard
          label="Avg Hours"
          value={isSummaryLoading ? '…' : `${stats.avgHours.toFixed(1)}h`}
          icon={Wallet}
          colorClass="bg-teal-50/60 dark:bg-teal-900/20"
          numClass="text-teal-700 dark:text-teal-400"
        />
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div className="space-y-4">
        <Tabs
          tabs={TABS.map((t) => ({
            key: t.id,
            label: (
              <span className="flex items-center gap-1.5">
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
              </span>
            ),
          }))}
          active={activeTab}
          onChange={(k) => {
            const next = k as TabId;
            if (next === 'logs' && !logsUnlocked && LOGS_PIN_CODE) {
              setShowLogsPinDialog(true);
              return;
            }
            if (next !== 'logs') setLogsUnlocked(false);
            setActiveTab(next);
          }}
        />

        {/* Tab body */}
        <div className="rounded-xl border border-border/60 bg-card shadow-sm p-4 space-y-4">
          {activeTab === 'punches' ? (
            <PunchesTab
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
            />
          ) : activeTab === 'monthly' ? (
            <MonthlyOverviewTab selectedUserId={selectedUserId} />
          ) : activeTab === 'miss-punch' ? (
            <MissPunchTab />
          ) : activeTab === 'penalty' ? (
            <PenaltyApprovalTab />
          ) : activeTab === 'paid-hours' ? (
            <PaidHoursTab />
          ) : activeTab === 'logs' ? (
            <LogsTab />
          ) : activeTab !== 'summary' ? (
            <ComingSoonTab
              label={TABS.find((t) => t.id === activeTab)?.label ?? ''}
            />
          ) : (
            <>
              {/* Section title */}
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-base font-semibold text-foreground">Attendance Report</h2>
              </div>

              {/* Controls row */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                {/* Search */}
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search by name or employee code…"
                  containerClassName="w-full max-w-xs"
                />

                {/* Right controls */}
                <div className="flex flex-wrap items-center gap-2 ml-auto">
                  {/* User filter */}
                  <UserFilterSelect
                    usersList={usersList}
                    isLoading={isUsersLoading}
                    isError={isUsersError}
                    selectedUserId={selectedUserId}
                    onChange={setSelectedUserId}
                  />

                  {/* Date navigator */}
                  <div className="flex items-center gap-1 rounded-lg border border-border/60 bg-card px-1 py-1">
                    <button
                      onClick={() => setSelectedDate((d) => navigateDate(d, -1))}
                      className="rounded-md p-1 text-muted-foreground hover:bg-muted transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-1.5 rounded-md px-2 py-0.5 hover:bg-muted transition-colors"
                        >
                          <Calendar className="h-3.5 w-3.5 text-primary" />
                          <span className="text-sm font-medium text-foreground whitespace-nowrap">
                            {formatDisplayDate(selectedDate)}
                          </span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="center">
                        <CalendarPicker
                          mode="single"
                          selected={parseISO(selectedDate)}
                          onSelect={(d) => {
                            if (!d) return;
                            setSelectedDate(format(d, 'yyyy-MM-dd'));
                            setIsDatePickerOpen(false);
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <button
                      onClick={() => setSelectedDate((d) => navigateDate(d, 1))}
                      className="rounded-md p-1 text-muted-foreground hover:bg-muted transition-colors"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Export */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleExport}
                    className="gap-1.5"
                    disabled={filtered.length === 0}
                  >
                    <Download className="h-4 w-4" />
                    Export
                  </Button>
                </div>
              </div>

              {/* Status filter pills */}
              <div className="flex flex-wrap gap-2">
                {[
                  { value: '',             label: 'All' },
                  { value: 'present',      label: 'Present' },
                  { value: 'late',         label: 'Late' },
                  { value: 'half_day',     label: 'Half Day' },
                  { value: 'absent',       label: 'Absent' },
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setStatusFilter(value)}
                    className={[
                      'rounded-full px-3 py-1 text-xs font-medium border transition-colors',
                      statusFilter === value
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-card text-muted-foreground border-border/60 hover:border-teal-400 hover:text-teal-700',
                    ].join(' ')}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* ── List body ─────────────────────────────────────────────── */}
              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}
                </div>
              ) : isError ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-red-100 bg-red-50 py-16 text-center">
                  <XCircle className="mb-3 h-10 w-10 text-red-400" />
                  <p className="font-semibold text-red-600">Failed to load attendance records</p>
                  <p className="mt-1 text-sm text-red-400">Check your connection and try again</p>
                  <Button size="sm" variant="outline" className="mt-4" onClick={() => void refetch()}>
                    Retry
                  </Button>
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 bg-card/50 py-20 text-center">
                  <Activity className="mb-3 h-10 w-10 text-muted-foreground/30" />
                  <p className="text-sm font-medium text-muted-foreground">
                    {search ? 'No employees match your search' : 'No attendance records for this date'}
                  </p>
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      className="mt-2 text-xs text-teal-600 hover:underline"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {filtered.map((record) => (
                    <EmployeeAttendanceRow
                      key={record.id}
                      record={record}
                      onSummary={setSummaryTarget}
                      onTimeline={setTimelineTarget}
                      onAddPunch={setAddPunchTarget}
                    />
                  ))}
                </div>
              )}

              {/* Row count */}
              {data && filtered.length > 0 && (
                <div className="border-t border-border/40 pt-4 text-sm text-muted-foreground">
                  Showing all {data.pagination.total} employees
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {showPaidHoursModal && (
        <CompanyPaidHoursModal onClose={() => setShowPaidHoursModal(false)} />
      )}
      {showPenaltyModal && (
        <ApplyPenaltyModal onClose={() => setShowPenaltyModal(false)} />
      )}
      <LogsPinDialog
        open={showLogsPinDialog}
        onVerified={() => {
          setLogsUnlocked(true);
          setShowLogsPinDialog(false);
          setActiveTab('logs');
        }}
        onCancel={() => setShowLogsPinDialog(false)}
      />
      {summaryTarget && (
        <AttendanceSummaryModal
          record={summaryTarget}
          date={selectedDate}
          onClose={() => setSummaryTarget(null)}
        />
      )}
      {timelineTarget && (
        <PunchTimelineModal
          userId={timelineTarget.user_id}
          userName={[timelineTarget.first_name, timelineTarget.last_name].filter(Boolean).join(' ') || '–'}
          photoUrl={timelineTarget.photo_url ?? undefined}
          date={selectedDate}
          onClose={() => setTimelineTarget(null)}
        />
      )}
      {addPunchTarget && (
        <AddPunchModal
          userId={addPunchTarget.user_id}
          userName={[addPunchTarget.first_name, addPunchTarget.last_name].filter(Boolean).join(' ') || '–'}
          date={selectedDate}
          onClose={() => setAddPunchTarget(null)}
          onSuccess={() => void refetch()}
        />
      )}
    </div>
  );
}
